import type { SortBy } from '@api/inventory';

/**
 * The Sort By ordering shared by every inventory-shaped grid, expressed once.
 *
 * Each grid names its two numbers differently — the DoH view reads `overall.drr` /
 * `overall.doh`, Inventory health reads `drr` / `coverDays` — so the rule takes accessors
 * rather than a row shape. What must not vary is the null handling: a row with no demand
 * has no cover figure at all, and that is NOT the same as running out today. It sinks to
 * the bottom under both LOW_STOCK and OVER_STOCK rather than leading either, matching the
 * Snapshot's server-side comparator (`warehouse-stock-info.service.ts`).
 *
 * DEFAULT returns undefined, meaning "leave the caller's own order alone" — each grid has
 * its own idea of a sensible default (server DRR-desc, alphabetical, worst-shortfall-first)
 * and this is not the place to override it.
 */

export type SortAccessors<T> = {
    /** Demand per day. Higher is "top selling". */
    rateOf: (row: T) => number;
    /** Days of cover. Null means no demand, so no cover figure exists. */
    coverOf: (row: T) => number | null | undefined;
};

const compareCover = <T,>(
    a: T,
    b: T,
    coverOf: SortAccessors<T>['coverOf'],
    direction: 1 | -1,
): number => {
    const aCover = coverOf(a);
    const bCover = coverOf(b);
    const aMissing = aCover === null || aCover === undefined;
    const bMissing = bCover === null || bCover === undefined;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    return (aCover - bCover) * direction;
};

export const compareBySortBy = <T,>(
    sortBy: SortBy,
    { rateOf, coverOf }: SortAccessors<T>,
): ((a: T, b: T) => number) | undefined => {
    switch (sortBy) {
        case 'TOP_SELLING':
            // Highest run rate first.
            return (a, b) => rateOf(b) - rateOf(a);

        case 'LOW_STOCK':
            // Least cover first, nulls last.
            return (a, b) => compareCover(a, b, coverOf, 1);

        case 'OVER_STOCK':
            // Most cover first, nulls last.
            return (a, b) => compareCover(a, b, coverOf, -1);

        default:
            return undefined;
    }
};
